import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import LlmCard from "./LlmCard";
import BlackFridayCard from "./BlackFridayCard";
import { isBlackFridayCard } from "../../../../utils/blackFriday";
import PromotionCreatePromo from "../../../../pages/promotions/PromotionCreatePromo";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import { postActionCentreCardAction } from "../../../../api";
import {
  mapDerivedPromoDetailToPromo,
  extractCalculationType,
} from "../../../../utils/derivedRatesMapper";
import { PROMO_OBJECT } from "../../../../pages/promotions/constants";
import { safeParseJson, deepClone } from "../../../../constants/utils";
import {
  deepMergeWithTypes,
  sanitizeRuleVO,
} from "../../../../utils/promoObjectMapper";
import { useIsPilotHotel } from "../../../../contexts/PropertyStoreContext";
import useCreatePromoDrawer from "../../../../hooks/useCreatePromoDrawer";
import { CARD_ACTION, CTA_ENUM, getPromoCategory } from "./llmCardMeta";

// discountType 1 = "Select days of week", the mode that reveals the day-specific
// inputs a DOW promo needs. v1 PromoCard forces it on its non-pilot path.
const DOW_DISCOUNT_TYPE = 1;

// Existing app routes the "Manage Rates" CTAs deep-link into.
const RATE_PLANS_ROUTE = "/property-config/rate-plans";
const AVAILABILITY_ROUTE = "/ari";

/**
 * Smart wrapper around {@link LlmCard} (Action Centre v2, EO-1217). Presentation
 * lives in LlmCard; this component wires each card's CTA to the SAME flows the v1
 * cards use — Create Promo opens the shared PromotionCreatePromo modal seeded from
 * the card's promo_object_dr; Review Rate Plans / Availability navigate to the
 * existing pages. No new action endpoints are introduced.
 */
const LlmActionCard = ({
  card,
  selectedProperty,
  onDismiss,
  onPromoUsed,
  cardPosition,
  hotelName,
  autoOpenCta = false,
  onCtaAutoOpened,
}) => {
  const navigate = useNavigate();
  // Selects which create-promo path the drawer takes, and is forwarded to it so the
  // submission API matches the state we seeded — same contract as v1 PromoCard.
  const isPilotHotel = useIsPilotHotel();

  const drawer = useCreatePromoDrawer();

  const openCreatePromo = async () => {
    // promo_object_dr comes through as a raw JSON string (same shape as v1's
    // promoObjectDr); reuse the shared safeParseJson helper to parse it.
    const promoObject = safeParseJson(card?.promoObjectDr);

    // useCreatePromoState routes a save through createSmartPromo only when BOTH
    // suggestionPromoId and promoCategory are set; without the category the promo
    // saves as a plain one and is never linked back to the card that suggested it.
    // Null for a theme with no create-promo category — see getPromoCategory.
    const promoCategory = getPromoCategory(card);

    // Branch on pilot status exactly as v1 PromoCard does, so the state we seed
    // always matches the isPilotHotel flag the drawer submits under.
    if (isPilotHotel) {
      // ── Pilot: the derived-rate snapshot lives under promoObject.snapshot ──
      // (fall back to the object itself if it isn't nested under `snapshot`).
      const snapshot = promoObject?.snapshot || promoObject || {};

      const mapped = mapDerivedPromoDetailToPromo(snapshot);
      // Treat as a brand-new promo (CREATE, not UPDATE).
      delete mapped._derivedRateMeta;
      mapped.suggestionPromoId = card?.promoId;
      if (promoCategory) mapped.promoCategory = promoCategory;

      drawer.open(mapped, extractCalculationType(snapshot));
    } else {
      // ── Legacy (non-pilot) path, mirroring v1 PromoCard ──
      // NOTE: the ACv2 pipeline only ever writes promo_object_dr — there is no
      // legacy promo_object column on action_centre_card — so the merge below
      // currently always falls through to the bare PROMO_OBJECT template and the
      // drawer opens unseeded. That is deliberate: it keeps a non-pilot hotel off
      // the derived-rate mapping, which its isPilotHotel=false submission cannot
      // honour. Seeding this properly needs a backend change (see EO-1217).
      const merged = deepMergeWithTypes(deepClone(PROMO_OBJECT), null);

      // Ensure all ruleVO fields have correct types for the backend DTO.
      sanitizeRuleVO(merged.rateRule?.ruleVO);

      // DOW promos need "Select days of week" mode to show day-specific inputs.
      if (
        String(card?.theme || "").toLowerCase() === "dow" &&
        merged.rateRule?.ruleVO?.discountVO
      ) {
        merged.rateRule.ruleVO.discountVO.discountType = DOW_DISCOUNT_TYPE;
      }

      drawer.open({
        ...merged,
        suggestionPromoId: card?.promoId,
        ...(promoCategory ? { promoCategory } : {}),
      });
    }

    await drawer.loadRatePlansAndRooms(selectedProperty?.propertyId);
  };

  /**
   * Records this card's outcome in action_centre_card_state. One endpoint covers both
   * outcomes; `action` decides the column — DISMISS writes dismissed_at, USED writes
   * used_at — so passing on a card is never counted as a conversion. The backend keys it
   * on recommendationId precisely so it works for every card type, not just the Create
   * Promo ones carrying a v1 promo_id.
   *
   * Only used_at suppresses the card on the next read; dismissed_at is recorded for
   * reporting only, so Maybe Later stays a session-only hide as it is in v1.
   *
   * Resolves true when the state is persisted (or there was nothing to persist).
   */
  const persistCardAction = async (action, logContext) => {
    const propertyId = selectedProperty?.propertyId;
    const recommendationId = card?.recommendationId;

    // Nothing to key the state on — nothing to persist, but don't block the caller.
    if (!propertyId || !recommendationId) return true;

    try {
      await postActionCentreCardAction({
        propertyId,
        recommendationIds: [recommendationId],
        action,
      });
      return true;
    } catch (error) {
      getLogger().error(logContext, {
        type: "Error",
        property_id: propertyId,
        recommendation_id: recommendationId,
        card_action: action,
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      return false;
    }
  };

  /**
   * "Maybe Later" — a session-only hide, exactly like the v1 cards, which just call onDismiss and
   * let ActionCenter hold the key in memory until the next reload. The DISMISS is still recorded so
   * dismissal rate stays reportable, but the v2 read does NOT filter on dismissed_at, so the card
   * comes back on the next load. Asking to be reminded later must not bury the card for good.
   *
   * Deliberately not awaited and never blocking: hiding is the user's intent, and a v1 Maybe Later
   * cannot fail. A recording failure is logged inside persistCardAction and otherwise ignored.
   */
  const handleDismiss = () => {
    persistCardAction(CARD_ACTION.DISMISS, "acv2.dismiss_failed");
    onDismiss?.();
  };

  /**
   * Runs only once a promo has actually been created from this card — the drawer's
   * recommendation-used hook, which the Cancel path never reaches. Deliberately NOT
   * setIsGetPromos: that is the drawer's hideModal, which Cancel also calls, so
   * persisting there would burn the card for anyone who opened the drawer and backed
   * out. The hook awaits this before hideModal triggers the parent's refetch, so the
   * state has landed by the time the v2 feed is re-read.
   */
  const handleRecommendationUsed = () =>
    persistCardAction(CARD_ACTION.USED, "acv2.promo_saved_state_failed");

  /**
   * The deep-link CTAs. Acting on the card IS the whole interaction here — there is
   * no later save to hang the state off, as there is for Create Promo — so record it
   * on the click. USED, not DISMISS: following the link is the conversion.
   *
   * Deliberately not awaited: react-router navigates client-side, so the request is
   * not torn down by leaving the page, and awaiting would stall the click behind a
   * round-trip for no user-visible gain. A failure is logged and leaves the card in
   * place — by then the user is on another page, where a toast is only noise.
   */
  const handleReviewCta = (route) => {
    persistCardAction(CARD_ACTION.USED, "acv2.cta_state_failed");
    navigate(route);
  };

  const handleCta = () => {
    switch (card?.ctaEnum) {
      case CTA_ENUM.CREATE_PROMO:
        // Recorded on save via onRecommendationUsed, NOT here: opening the drawer
        // and backing out must not cost the supplier the card.
        openCreatePromo();
        break;
      case CTA_ENUM.REVIEW_RATE_PLANS:
        handleReviewCta(RATE_PLANS_ROUTE);
        break;
      case CTA_ENUM.REVIEW_AVAILABILITY:
        handleReviewCta(AVAILABILITY_ROUTE);
        break;
      default:
        break;
    }
  };

  // EO-1892: the strip / pop-up CTA opens this card's CTA. The
  // guard stops a re-render reopening it while the request is still set; it
  // clears once the parent drops the request, so the next click opens again.
  const autoOpenedRef = useRef(null);
  useEffect(() => {
    if (!autoOpenCta) {
      autoOpenedRef.current = null;
      return;
    }
    if (autoOpenedRef.current === card?.recommendationId) return;
    autoOpenedRef.current = card?.recommendationId;
    handleCta();
    onCtaAutoOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCta, card?.recommendationId]);

  const CardView = isBlackFridayCard(card) ? BlackFridayCard : LlmCard;

  return (
    <>
      <CardView
        card={card}
        onDismiss={handleDismiss}
        onCta={handleCta}
        cardPosition={cardPosition}
        hotelName={hotelName}
      />
      {drawer.loader}
      {drawer.isOpen && (
        <PromotionCreatePromo
          {...drawer.drawerProps}
          property={selectedProperty}
          isPilotHotel={isPilotHotel}
          setIsGetPromos={onPromoUsed}
          // Records the card as actioned in action_centre_card_state instead of the
          // v1 mark-used endpoints, which are keyed on a v1 promo/recommendation id
          // this card does not carry. createSmartPromo attribution is unaffected.
          onRecommendationUsed={handleRecommendationUsed}
        />
      )}
    </>
  );
};

LlmActionCard.propTypes = {
  card: PropTypes.shape({
    // ACV2_<CRS>_<THEME>_<YYYYMMDD> — the key the v2 card state is stored under.
    recommendationId: PropTypes.string,
    ctaEnum: PropTypes.number,
    cta: PropTypes.string,
    theme: PropTypes.string,
    promoId: PropTypes.string,
    promoObjectDr: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  selectedProperty: PropTypes.object,
  onDismiss: PropTypes.func,
  onPromoUsed: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
  autoOpenCta: PropTypes.bool,
  onCtaAutoOpened: PropTypes.func,
};

export default LlmActionCard;
